import React, { useEffect, useState } from 'react';
import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { ITableHeadings, ITableMetadata } from '../../../common/interfaces/common.interface';
import BasicTable from '../../../components/BasicTable/BasicTable';
import { initialMetadataState, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import TableHeader from '../../../components/TableHeader';
import { ParentDto } from '../../../common/interfaces/kindergarten.interface';
import { ReactComponent as AddIcon } from '../../../assets/add-icon.svg';
import { fetchParents, updateParentStatus } from '../../../api/kindergarten.api';
import { createNotification } from '../../../utilities/helper/notification.helper';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as FaceIcon } from '../../../assets/face-icon.svg';
import { GeneralStatusEnum, ImportUserType, SortOrderEnum } from '../../../common/enums/common.enum';
import GenericInfoModal from '../../../components/GenericInfoModal';
import ToggleWithLabel from '../../../components/ToggleWithLabel';
import { ParentsTabSortKeyEnum } from '../../../common/enums/kindergarten.enum';
import GenericImportButton from '../../../components/GenericImportButton';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
export const PARENT_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'common.name', sortable: true, sortKey: ParentsTabSortKeyEnum.NAME, width: '20%' },
  { id: 2, value: '', label: 'common.email', sortable: true, sortKey: ParentsTabSortKeyEnum.EMAIL },
  { id: 3, value: '', label: 'common.phone', sortable: true, sortKey: ParentsTabSortKeyEnum.PHONE },
  { id: 4, value: '', label: 'kindergarten.tabs.children', sortable: true, sortKey: ParentsTabSortKeyEnum.CHILDREN, width: '30%' },
  { id: 5, value: '', label: 'common.status', sortable: true, sortKey: ParentsTabSortKeyEnum.STATUS },
  { id: 6, value: '', label: '', sortable: false }
];

const ParentsTab: React.FC<{ kindergartenId: string; kindergartenName: string }> = ({ kindergartenId, kindergartenName }) => {
  const { t } = useTranslation();
  const [parents, setParents] = useState<ParentDto[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<ITableMetadata>(initialMetadataState);
  const [withChildren, setWithChildren] = useState<boolean>(false);
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useState<string>('');
  const [sortBy, setSortBy] = useState<ParentsTabSortKeyEnum>();
  const [sortOrder, setSortOrder] = useState<SortOrderEnum>();
  const [parentToDeactivate, setParentToDeactivate] = useState<ParentDto | null>(null);

  useEffect(() => {
    loadParents();
  }, [kindergartenId, metadata.currentPage, metadata.itemsPerPage, searchParams, sortBy, sortOrder, withChildren]);

  const handleSearchChange = (newSearch: string) => {
    setMetadata(prev => ({ ...prev, currentPage: 1 }));
    setSearchParams(newSearch);
  };

  const handleSortChange = (sortKey: string | null, order: 'asc' | 'desc' | null) => {
    if (sortKey === 'children' && !withChildren) return;
    setSortBy(sortKey ? (sortKey as ParentsTabSortKeyEnum) : undefined);
    setSortOrder(order ? (order as SortOrderEnum) : undefined);
  };

  const loadParents = async () => {
    setLoader(true);
    try {
      const result = await fetchParents(
        kindergartenId,
        metadata.currentPage,
        metadata.itemsPerPage,
        sortBy,
        sortOrder,
        searchParams,
        withChildren
      );
      setParents(result.results ?? []);
      if (result.paginationInfo) {
        setMetadata(prev => ({
          ...prev,
          totalItems: result.paginationInfo?.totalItems ?? 0,
          totalPages: result.paginationInfo?.totalPages ?? 0
        }));
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('parents.loadFailed'));
    } finally {
      setLoader(false);
    }
  };
  const handleChangeParentStatus = async (parentId: string) => {
    if (!kindergartenId) return;

    try {
      await updateParentStatus(kindergartenId, parentId);
      createNotification(MESSAGE_TYPE.SUCCESS, t('parents.statusChanged'));
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('parents.statusChangeFailed'));
    }
  };

  const handleConfirmDeactivation = async () => {
    if (!parentToDeactivate) return;
    handleChangeParentStatus(parentToDeactivate.id);
    setParents(prev => prev.map(t => (t.id === parentToDeactivate.id ? { ...t, status: GeneralStatusEnum.INACTIVE } : t)));
    setParentToDeactivate(null);
  };

  const handleCreateClick = () => {
    navigate(`/${kindergartenId}/parent/create/${encodeURIComponent(kindergartenName)}`);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 24,
            color: 'text.primary',
            fontFamily: 'Poppins'
          }}
        >
          {t('kindergarten.tabs.parents')}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <GenericImportButton
            label={t('common.import')}
            type={ImportUserType.PARENT}
            kindergartenId={kindergartenId}
            onSuccessImport={loadParents}
          />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            label={t('common.create')}
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          border: `1px solid ${palette.line}`,
          borderRadius: 4
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <TableHeader searchTitle={t('common.searchByName')} sortOptions={[]} setSearchValue={handleSearchChange} />
          <ToggleWithLabel label={t('parents.withChildren')} checked={withChildren} onChange={setWithChildren} />
        </Box>
        <BasicTable
          headings={PARENT_TABLE_HEADINGS}
          loader={loader}
          metadata={metadata}
          setMetadata={setMetadata}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        >
          {parents.map(parent => (
            <TableRow
              key={parent.id}
              hover
              sx={{ cursor: ' pointer' }}
              onClick={() => navigate(`/${kindergartenId}/parent/${parent.id}/details/${encodeURIComponent(kindergartenName)}`)}
            >
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  {parent.profileImage ? (
                    <img
                      src={parent.profileImage}
                      alt={`${parent.firstName} ${parent.lastName}`}
                      style={{ color: 'text.primary', width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <FaceIcon style={{ width: 34, height: 34 }} />
                  )}
                  <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 400 }}>
                    {`${parent.firstName} ${parent.lastName}`}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{parent.email}</TableCell>
              <TableCell>{parent.phoneNumber || '-'}</TableCell>
              <TableCell>{parent.children?.map((p: any) => p.fullName).join(', ') ?? '-'}</TableCell>
              <TableCell>
                <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <GenericStatusSwitch
                    status={parent.status}
                    onChange={() => {
                      if (parent.status === GeneralStatusEnum.ACTIVE) {
                        setParentToDeactivate(parent);
                      } else {
                        handleChangeParentStatus(parent.id);
                        setParents(prev =>
                          prev.map(t =>
                            t.id === parent.id
                              ? {
                                  ...t,
                                  status: t.status === GeneralStatusEnum.ACTIVE ? GeneralStatusEnum.INACTIVE : GeneralStatusEnum.ACTIVE
                                }
                              : t
                          )
                        );
                      }
                    }}
                    showStatusText={false}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <EditIcon
                  style={{ cursor: 'pointer' }}
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`/${kindergartenId}/parent/${parent.id}/edit/${encodeURIComponent(kindergartenName)}`);
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
          {parentToDeactivate && (
            <GenericInfoModal
              open={true}
              onClose={() => setParentToDeactivate(null)}
              onConfirm={handleConfirmDeactivation}
              label={t('parents.deactivate')}
              message={t('common.confirmDeactivate')}
              confirmLabel={t('common.deactivate')}
              entityName={`${parentToDeactivate.firstName} ${parentToDeactivate.lastName}`}
            />
          )}
        </BasicTable>
      </Box>
    </Box>
  );
};
export default ParentsTab;
